/**
 * Replays every selection rule over stored results, so a change to the logic is judged on evidence
 * instead of intuition. Ported from `BacktestService`.
 *
 * **Strictly walk-forward.** Every signal for a match is computed from matches played BEFORE it,
 * and percentile ranks are taken against an expanding window of prior values — never against the
 * whole file, which would leak the future into the threshold.
 *
 * **Outcomes are recorded as a sequence and only priced after the loop**, because the fair price is
 * the base rate over the matches actually EVALUATED. Pricing off the full file (warmup included)
 * hands every strategy a free edge: it once made the baseline row read **+5.2% instead of zero**.
 * If a row named "every match" is ever non-zero, that is the bug and nothing below it is real.
 */
import { PropensityModel, FLAG_MARGIN } from './propensity';
import { over25, playedAt, under35, type MatchResult } from './types';

export const DEFAULT_WARMUP = 250;

/**
 * Floor for the adaptive warmup. A FIXED 250 starved every cup competition: Europa Conference has
 * 198 results in total, so it never cleared the bar and backtested to literally nothing, while
 * UCL/UEL/Sudamericana (~270) scored only the ~20 matches of overflow. Scaling the warmup to the
 * sample keeps big leagues at 250 and still gives short-history competitions a real evaluation.
 * Thin warmups are safe here because the propensity model shrinks toward the league mean, so a
 * sparse seed yields league-average probabilities rather than confident noise.
 */
export const MIN_WARMUP = 80;
const REBUILD_EVERY = 5;
const FORM_WINDOW = 5;
const SOT_WINDOW = 10;

export interface StrategyResult {
  key: string;
  name: string;
  description: string;
  bets: number;
  wins: number;
  /** Settlement price, 0-1: the market's observed base rate over the evaluated matches. */
  price: number;
  staked: number;
  returned: number;
  maxDrawdown: number;
  longestLosingStreak: number;
  /** False for compound strategies staking more than one leg, where one break-even is meaningless. */
  hasBreakEven: boolean;
}

export interface BacktestRun {
  matchesTotal: number;
  matchesScored: number;
  warmupMatches: number;
  fromUtc: string;
  toUtc: string;
  stakePerBet: number;
  strategies: StrategyResult[];
  flaggedHitRate: number;
  unflaggedHitRate: number;
  zScore: number;
  flagRate: number;
  quintileCounts: number[];
  quintileHits: number[];
}

export const hitRate = (s: StrategyResult) => (s.bets > 0 ? (100 * s.wins) / s.bets : 0);
export const breakEvenRate = (s: StrategyResult) => (s.price > 0 ? s.price * 100 : 0);
/** Percentage points of hit rate above break-even. This is the whole game. */
export const edgePoints = (s: StrategyResult) => hitRate(s) - breakEvenRate(s);
export const pnl = (s: StrategyResult) => s.returned - s.staked;
export const roi = (s: StrategyResult) => (s.staked > 0 ? (100 * pnl(s)) / s.staked : 0);

/** Below this a row is a coin story, not a result. */
export const MINIMUM_BETS = 50;
export const isUnderSampled = (s: StrategyResult) => s.bets < MINIMUM_BETS;

/** z = (wins − n·p) / √(n·p(1−p)) against the break-even rate. */
export function strategyZ(s: StrategyResult): number {
  if (!s.hasBreakEven || s.bets === 0 || s.price <= 0 || s.price >= 1) return 0;
  const expected = s.bets * s.price;
  const sd = Math.sqrt(s.bets * s.price * (1 - s.price));
  return sd > 1e-9 ? (s.wins - expected) / sd : 0;
}

export const liftPoints = (r: BacktestRun) => r.flaggedHitRate - r.unflaggedHitRate;
export const isSignificant = (r: BacktestRun) => Math.abs(r.zScore) >= 1.96;

/** True while every "every match" baseline still returns exactly zero, as it must. */
export const baselinesIntact = (r: BacktestRun) =>
  r.strategies.filter((s) => s.name.includes('every match')).every((s) => Math.abs(roi(s)) < 0.05);

function poisson(lambda: number, k: number): number {
  let p = Math.exp(-lambda);
  for (let i = 1; i <= k; i++) p *= lambda / i;
  return p;
}

/** Rank of `value` among prior values, 0-1. 0.5 until any history exists. */
function percentile(prior: number[], value: number): number {
  if (prior.length === 0) return 0.5;
  let below = 0;
  for (const v of prior) if (v < value) below++;
  return below / prior.length;
}

type History = Map<string, MatchResult[]>;

function remember(hist: History, r: MatchResult): void {
  const add = (team: string) => {
    if (!team) return;
    const k = team.toLowerCase();
    const list = hist.get(k);
    if (list) list.push(r);
    else hist.set(k, [r]);
  };
  add(r.homeTeam);
  add(r.awayTeam);
}

function recent(hist: History, team: string, n: number): MatchResult[] {
  const l = hist.get((team ?? '').toLowerCase());
  if (!l?.length) return [];
  return l.slice(Math.max(0, l.length - n));
}

/** Mean of both sides' recent total-goals averages. Null until both have a record. */
function formTotal(hist: History, home: string, away: string): number | null {
  const h = recent(hist, home, FORM_WINDOW);
  const a = recent(hist, away, FORM_WINDOW);
  if (h.length === 0 || a.length === 0) return null;
  const mean = (xs: MatchResult[]) => xs.reduce((s, x) => s + x.totalGoals, 0) / xs.length;
  return (mean(h) + mean(a)) / 2;
}

/**
 * Expected shots on target: each side's rolling SOT-for blended with the opponent's SOT-conceded.
 * Shots are a less noisy signal than goals, so this can separate fixtures goals alone cannot.
 */
function expectedSot(hist: History, home: string, away: string): number | null {
  const h = recent(hist, home, SOT_WINDOW);
  const a = recent(hist, away, SOT_WINDOW);
  if (h.length === 0 || a.length === 0) return null;

  const side = (list: MatchResult[], team: string, forTeam: boolean): number | null => {
    const vals: number[] = [];
    for (const r of list) {
      const isHome = r.homeTeam?.toLowerCase() === team.toLowerCase();
      const sot = forTeam
        ? isHome
          ? r.homeShotsOnTarget
          : r.awayShotsOnTarget
        : isHome
          ? r.awayShotsOnTarget
          : r.homeShotsOnTarget;
      if (sot !== null && sot !== undefined) vals.push(sot);
    }
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };

  const hFor = side(h, home, true);
  const hAgainst = side(h, home, false);
  const aFor = side(a, away, true);
  const aAgainst = side(a, away, false);
  if (hFor === null || aFor === null || hAgainst === null || aAgainst === null) return null;

  return (hFor + aAgainst) / 2 + (aFor + hAgainst) / 2;
}

/** Days since whichever side played most recently — the congested one drives fatigue. */
function minRestDays(hist: History, m: MatchResult): number | null {
  const rest = (team: string): number | null => {
    const last = recent(hist, team, 1)[0];
    if (!last) return null;
    const t = playedAt(last);
    if (!Number.isFinite(t)) return null;
    return Math.trunc((playedAt(m) - t) / 86_400_000);
  };
  const h = rest(m.homeTeam);
  const a = rest(m.awayTeam);
  if (h === null && a === null) return null;
  return Math.min(h ?? Number.MAX_SAFE_INTEGER, a ?? Number.MAX_SAFE_INTEGER);
}

/** True when this team's last three matches all went over 2.5. */
function overStreak(hist: History, team: string): boolean {
  const last3 = recent(hist, team, 3);
  return last3.length === 3 && last3.every(over25);
}

/** Turns a win/loss sequence into P&L, drawdown and the longest losing run. */
function price(
  key: string,
  name: string,
  description: string,
  outcomes: boolean[],
  p: number,
  stake: number,
): StrategyResult {
  let wins = 0;
  let streak = 0;
  let worstStreak = 0;
  let cum = 0;
  let peak = 0;
  let maxDd = 0;
  const payout = p > 0 ? stake / p : 0;

  for (const won of outcomes) {
    if (won) {
      wins++;
      cum += payout - stake;
      streak = 0;
    } else {
      cum -= stake;
      streak++;
      if (streak > worstStreak) worstStreak = streak;
    }
    if (cum > peak) peak = cum;
    const dd = peak - cum;
    if (dd > maxDd) maxDd = dd;
  }

  return {
    key,
    name,
    description,
    bets: outcomes.length,
    wins,
    price: p,
    staked: outcomes.length * stake,
    returned: wins * payout,
    maxDrawdown: maxDd,
    longestLosingStreak: worstStreak,
    hasBreakEven: true,
  };
}

export function runBacktest(
  results: readonly MatchResult[],
  stakePerBet = 10,
  warmupOverride?: number,
): BacktestRun {
  const ordered = results
    .filter((r) => Number.isFinite(playedAt(r)) && (r.homeTeam?.length ?? 0) > 0)
    .slice()
    .sort((a, b) => playedAt(a) - playedAt(b));

  // C# integer division, then clamped. Keep the truncation — it moves the warmup by up to two
  // matches on a small sample, which is enough to change a scored count.
  const warmup =
    warmupOverride ?? Math.min(DEFAULT_WARMUP, Math.max(MIN_WARMUP, Math.trunc(ordered.length / 3)));

  const empty: BacktestRun = {
    matchesTotal: ordered.length,
    matchesScored: 0,
    warmupMatches: warmup,
    fromUtc: '',
    toUtc: '',
    stakePerBet,
    strategies: [],
    flaggedHitRate: 0,
    unflaggedHitRate: 0,
    zScore: 0,
    flagRate: 0,
    quintileCounts: [0, 0, 0, 0, 0],
    quintileHits: [0, 0, 0, 0, 0],
  };
  if (ordered.length <= warmup) return empty;

  const legs = new Map<string, boolean[]>();
  const leg = (key: string): boolean[] => {
    let l = legs.get(key);
    if (!l) {
      l = [];
      legs.set(key, l);
    }
    return l;
  };

  // Straddle stakes two legs, so it records both outcomes rather than a single win/lose.
  const straddle: { over: boolean; under: boolean }[] = [];
  // Result picks carry which side was backed — home, draw and away have different fair prices, so
  // pricing them uniformly (the old bug priced every pick at the HOME rate) misstates P&L.
  const resultPicks: { won: boolean; side: 'H' | 'D' | 'A' }[] = [];

  // Expanding-window distributions, so a percentile only ever sees the past.
  const totalDist: number[] = [];
  const exact3Dist: number[] = [];
  const formDist: number[] = [];
  const sotDist: number[] = [];
  const gapDist: number[] = [];

  const teamHistory: History = new Map();

  let flagged = 0;
  let flaggedHit = 0;
  let unflagged = 0;
  let unflaggedHit = 0;
  let scored = 0;
  let evalBtts = 0;
  let evalOver25 = 0;
  let evalUnder35 = 0;
  let evalHome = 0;
  let evalDraw = 0;
  let evalAway = 0;
  const qCount = [0, 0, 0, 0, 0];
  const qHits = [0, 0, 0, 0, 0];

  // Seed team history with the warmup slice.
  for (let i = 0; i < warmup; i++) remember(teamHistory, ordered[i]);

  let model: PropensityModel | null = null;
  for (let i = warmup; i < ordered.length; i++) {
    if (model === null || (i - warmup) % REBUILD_EVERY === 0) {
      model = PropensityModel.build(ordered.slice(0, i));
    }

    const m = ordered[i];
    const lambdas = model.expectedGoals(m.homeTeam, m.awayTeam);
    const bttsPct = model.percentileOf(m.homeTeam, m.awayTeam);

    if (lambdas === null || bttsPct === null) {
      remember(teamHistory, m);
      continue;
    }

    scored++;
    const total = lambdas.home + lambdas.away;
    const gap = Math.abs(lambdas.home - lambdas.away);

    const pOver25 = 1 - poisson(total, 0) - poisson(total, 1) - poisson(total, 2);
    const pUnder35 =
      poisson(total, 0) + poisson(total, 1) + poisson(total, 2) + poisson(total, 3);
    const pExact3 = poisson(total, 3);
    const pBtts = model.propensity(m.homeTeam, m.awayTeam) ?? 0;

    // Percentiles against prior matches only.
    const totalPct = percentile(totalDist, total);
    const exact3Pct = percentile(exact3Dist, pExact3);
    const gapPct = percentile(gapDist, gap);

    const form = formTotal(teamHistory, m.homeTeam, m.awayTeam);
    const formPct = form !== null ? percentile(formDist, form) : null;

    const xsot = expectedSot(teamHistory, m.homeTeam, m.awayTeam);
    const sotPct = xsot !== null ? percentile(sotDist, xsot) : null;

    const restDays = minRestDays(teamHistory, m);
    const dow = new Date(playedAt(m)).getUTCDay();
    const midweek = dow === 2 || dow === 3 || dow === 4; // Tue / Wed / Thu
    const streakOver =
      overStreak(teamHistory, m.homeTeam) || overStreak(teamHistory, m.awayTeam);

    const isBttsFlagged = model.isLikelyBtts(m.homeTeam, m.awayTeam);
    if (isBttsFlagged) {
      flagged++;
      if (m.bothTeamsScored) flaggedHit++;
    } else {
      unflagged++;
      if (m.bothTeamsScored) unflaggedHit++;
    }

    const q = Math.min(4, Math.trunc(bttsPct * 5));
    qCount[q]++;
    if (m.bothTeamsScored) qHits[q]++;

    if (m.bothTeamsScored) evalBtts++;
    if (over25(m)) evalOver25++;
    if (under35(m)) evalUnder35++;
    const homeWon = m.homeGoals > m.awayGoals;
    const drew = m.homeGoals === m.awayGoals;
    const awayWon = m.awayGoals > m.homeGoals;
    if (homeWon) evalHome++;
    if (drew) evalDraw++;
    if (awayWon) evalAway++;

    // ---- match result and BTTS No ----
    leg('home_all').push(homeWon);
    leg('draw_all').push(drew);
    leg('away_all').push(awayWon);
    leg('bttsno_all').push(!m.bothTeamsScored);

    const res = model.probabilities(m.homeTeam, m.awayTeam);
    if (res !== null) {
      // Only the strongest result line is ever taken — they sum to 1, so backing more than one is a
      // guaranteed loss. Mirrors the live selector exactly.
      const best = Math.max(res.homeWin, Math.max(res.draw, res.awayWin));
      if (res.homeWin === best && model.flagsHomeWin(best)) resultPicks.push({ won: homeWon, side: 'H' });
      else if (res.draw === best && model.flagsDraw(best)) resultPicks.push({ won: drew, side: 'D' });
      else if (res.awayWin === best && model.flagsAwayWin(best)) resultPicks.push({ won: awayWon, side: 'A' });

      if (model.flagsHomeWin(res.homeWin)) leg('home_model').push(homeWon);
      if (model.flagsAwayWin(res.awayWin)) leg('away_model').push(awayWon);
      if (model.flagsDraw(res.draw)) leg('draw_model').push(drew);
      if (model.flagsBttsNo(res.bttsNo)) leg('bttsno_model').push(!m.bothTeamsScored);
    }

    // ---------- baselines ----------
    leg('btts_all').push(m.bothTeamsScored);
    leg('over_all').push(over25(m));
    leg('under_all').push(under35(m));

    // ---------- BTTS ranking ----------
    if (isBttsFlagged) leg('btts_flagged').push(m.bothTeamsScored);
    else leg('btts_unflagged').push(m.bothTeamsScored);
    if (bttsPct >= 0.8) leg('btts_top20').push(m.bothTeamsScored);

    // ---------- A: totals from the same Poisson model ----------
    // Flag slices sized to each market's own base rate, mirroring the BTTS mechanism. League-relative
    // throughout — the model carries this league's rates, measured walk-forward from earlier matches.
    if (totalPct >= 1 - model.leagueOver25Rate) leg('over_flagged').push(over25(m));
    else leg('over_control').push(over25(m));
    if (totalPct >= 0.8) leg('over_top20').push(over25(m));

    if (totalPct <= model.leagueUnder35Rate) leg('under_flagged').push(under35(m));
    else leg('under_control').push(under35(m));
    if (totalPct <= 0.2) leg('under_bottom20').push(under35(m));

    // A6 — absolute-style calibration, but the bar is base rate + margin, never a constant.
    if (pBtts >= model.leagueBttsRate + FLAG_MARGIN) leg('btts_abs').push(m.bothTeamsScored);
    if (model.flagsOver25(pOver25)) leg('over_abs').push(over25(m));
    if (model.flagsUnder35(pUnder35)) leg('under_abs').push(under35(m));

    // A7 — the exactly-3 straddle: both legs, wins both only on exactly 3 goals.
    if (exact3Pct >= 0.9) straddle.push({ over: over25(m), under: under35(m) });

    // ---------- B: rolling windows ----------
    // Every nullable comparison is explicit: in C# `null >= x` is false, but in JS `null <= 0.30`
    // coerces to `0 <= 0.30` and is TRUE. Left implicit, the two "cold form" rules would fire on
    // every fixture with no history and the port would silently disagree with the original.
    if (formPct !== null && formPct >= 0.7) leg('form_over').push(over25(m));
    if (formPct !== null && formPct <= 0.3) leg('form_under').push(under35(m));
    if (sotPct !== null && sotPct >= 0.7) {
      leg('sot_over').push(over25(m));
      leg('sot_btts').push(m.bothTeamsScored);
    }
    if (restDays !== null && restDays <= 4) leg('rest_over').push(over25(m));
    if (midweek) leg('midweek_over').push(over25(m));
    else leg('weekend_over').push(over25(m));
    if (streakOver) {
      leg('streak_under').push(under35(m));
      leg('streak_over').push(over25(m));
    }

    // ---------- C: confluence ----------
    if (isBttsFlagged && totalPct >= 0.6) leg('conf_btts').push(m.bothTeamsScored);
    if (isBttsFlagged && totalPct <= 0.4) leg('disagree_btts').push(m.bothTeamsScored);
    if (gapPct >= 0.8) leg('blowout_over').push(over25(m));
    if (gap < 0.35 && total < model.leagueHomeGoalsAvg + model.leagueAwayGoalsAvg) {
      leg('cagey_under').push(under35(m));
    }

    // Record signals for future percentiles, then fold the match into team history.
    totalDist.push(total);
    exact3Dist.push(pExact3);
    gapDist.push(gap);
    if (form !== null) formDist.push(form);
    if (xsot !== null) sotDist.push(xsot);
    remember(teamHistory, m);
  }

  const denom = Math.max(scored, 1);
  const priceBtts = evalBtts / denom;
  const priceOver = evalOver25 / denom;
  const priceUnder = evalUnder35 / denom;
  const priceHome = evalHome / denom;
  const priceDraw = evalDraw / denom;
  const priceAway = evalAway / denom;
  const priceBttsNo = 1 - priceBtts;

  const make = (key: string, name: string, desc: string, p: number) =>
    price(key, name, desc, legs.get(key) ?? [], p, stakePerBet);

  const strategies: StrategyResult[] = [
    make('btts_all', 'BTTS — every match', 'Baseline. Must return 0.0% or the harness is broken.', priceBtts),
    make('btts_flagged', 'BTTS — propensity flagged', 'Top slice by team scoring/conceding history.', priceBtts),
    make('btts_top20', 'BTTS — top 20% only', 'Strongest fifth by propensity.', priceBtts),
    make('btts_unflagged', 'BTTS — unflagged (control)', 'The rejects. Should mirror the flagged row.', priceBtts),
    make('btts_abs', 'BTTS — model ≥65% (absolute)', 'Ignores ranking; bets only a clear absolute estimate.', priceBtts),
    make('conf_btts', 'BTTS — confluence', 'BTTS-flagged AND an above-median expected total.', priceBtts),
    make('disagree_btts', 'BTTS — models disagree', 'BTTS-flagged but a LOW total: goals spread thin, 1-1 shapes.', priceBtts),
    make('sot_btts', 'BTTS — high expected SOT', 'Shots on target are less noisy than goals.', priceBtts),

    make('over_all', 'Over 2.5 — every match', 'Baseline. Must return 0.0%.', priceOver),
    make('over_flagged', 'Over 2.5 — high-total flagged', 'Top slice by Poisson expected total.', priceOver),
    make('over_top20', 'Over 2.5 — top 20%', 'Highest fifth of expected totals.', priceOver),
    make('over_control', 'Over 2.5 — control', 'The rejects of the high-total flag.', priceOver),
    make('over_abs', 'Over 2.5 — model ≥68%', 'Absolute calibration rather than ranking.', priceOver),
    make('form_over', 'Over 2.5 — hot recent form', "Both sides' last-5 goal totals in the top slice.", priceOver),
    make('sot_over', 'Over 2.5 — high expected SOT', 'Rolling shots-on-target for and against.', priceOver),
    make('rest_over', 'Over 2.5 — short rest (≤4 days)', 'Tests the fixture-congestion story.', priceOver),
    make('midweek_over', 'Over 2.5 — midweek (Tue–Thu)', 'Tests the midweek pattern we observed.', priceOver),
    make('weekend_over', 'Over 2.5 — weekend', 'The other half of the midweek test.', priceOver),
    make('blowout_over', 'Over 2.5 — mismatch fixtures', 'Largest strength gaps: blowouts carry goals.', priceOver),
    make('streak_over', 'Over 2.5 — momentum', 'A side on a 3-match over run. Must lose if regression wins.', priceOver),

    make('under_all', 'Under 3.5 — every match', 'Baseline. Must return 0.0%.', priceUnder),
    make('under_flagged', 'Under 3.5 — low-total flagged', 'Bottom slice by expected total.', priceUnder),
    make('under_bottom20', 'Under 3.5 — bottom 20%', 'Lowest fifth of expected totals.', priceUnder),
    make('under_control', 'Under 3.5 — control', 'The rejects of the low-total flag.', priceUnder),
    make('under_abs', 'Under 3.5 — model ≥65%', 'Absolute calibration rather than ranking.', priceUnder),
    make('form_under', 'Under 3.5 — cold recent form', "Both sides' last-5 goal totals in the bottom slice.", priceUnder),
    make('cagey_under', 'Under 3.5 — evenly matched & low', 'Near-equal strengths, both below average.', priceUnder),
    make('streak_under', 'Under 3.5 — regression', 'Fades a 3-match over run. Mirror of momentum.', priceUnder),

    make('bttsno_all', 'BTTS No — every match', 'Baseline. Must return 0.0%.', priceBttsNo),
    make('bttsno_model', 'BTTS No — model threshold', 'Low-scoring profile by team history.', priceBttsNo),

    make('home_all', 'Home win — every match', 'Baseline. Must return 0.0%.', priceHome),
    make('draw_all', 'Draw — every match', 'Baseline. Must return 0.0%.', priceDraw),
    make('away_all', 'Away win — every match', 'Baseline. Must return 0.0%.', priceAway),
    make('home_model', 'Home win — base+8 margin', 'Poisson result model on team strengths.', priceHome),
    make('draw_model', 'Draw — base+5 margin', 'Evenly matched, low-scoring shapes.', priceDraw),
    make('away_model', 'Away win — base+8 margin', 'Away side rated clear favourite.', priceAway),
  ];

  if (resultPicks.length > 0) {
    strategies.push(priceResultPicks(resultPicks, priceHome, priceDraw, priceAway, stakePerBet));
  }
  if (straddle.length > 0) {
    strategies.push(priceStraddle(straddle, priceOver, priceUnder, stakePerBet));
  }

  const p1 = flagged > 0 ? flaggedHit / flagged : 0;
  const p2 = unflagged > 0 ? unflaggedHit / unflagged : 0;
  const pooled =
    flagged + unflagged > 0 ? (flaggedHit + unflaggedHit) / (flagged + unflagged) : 0;
  const se = Math.sqrt(
    pooled * (1 - pooled) * (1 / Math.max(flagged, 1) + 1 / Math.max(unflagged, 1)),
  );

  return {
    matchesTotal: ordered.length,
    matchesScored: scored,
    warmupMatches: warmup,
    fromUtc: new Date(playedAt(ordered[warmup])).toISOString(),
    toUtc: new Date(playedAt(ordered[ordered.length - 1])).toISOString(),
    stakePerBet,
    strategies,
    flaggedHitRate: p1 * 100,
    unflaggedHitRate: p2 * 100,
    zScore: se > 1e-9 ? (p1 - p2) / se : 0,
    flagRate: scored > 0 ? (100 * flagged) / scored : 0,
    quintileCounts: qCount,
    quintileHits: qHits,
  };
}

/**
 * Prices the strongest-side result strategy. Each pick settles at ITS OWN side's base rate — a draw
 * pick against the draw rate, an away pick against the away rate. No single break-even applies, so
 * the row reports P&L without one.
 */
function priceResultPicks(
  picks: { won: boolean; side: 'H' | 'D' | 'A' }[],
  priceHome: number,
  priceDraw: number,
  priceAway: number,
  stake: number,
): StrategyResult {
  let wins = 0;
  let streak = 0;
  let worstStreak = 0;
  let cum = 0;
  let peak = 0;
  let maxDd = 0;
  let returned = 0;

  for (const { won, side } of picks) {
    const p = side === 'H' ? priceHome : side === 'D' ? priceDraw : priceAway;
    const payout = p > 0 ? stake / p : 0;
    if (won) {
      wins++;
      returned += payout;
      cum += payout - stake;
      streak = 0;
    } else {
      cum -= stake;
      streak++;
      if (streak > worstStreak) worstStreak = streak;
    }
    if (cum > peak) peak = cum;
    if (peak - cum > maxDd) maxDd = peak - cum;
  }

  return {
    key: 'result_best',
    name: 'Result — strongest side only',
    description:
      'Backs whichever of home/draw/away the model rates highest (base + margin), ' +
      "each pick priced at its own side's base rate.",
    bets: picks.length,
    wins,
    price: 0,
    hasBreakEven: false,
    staked: picks.length * stake,
    returned,
    maxDrawdown: maxDd,
    longestLosingStreak: worstStreak,
  };
}

/** Prices the exactly-3 straddle, which stakes both totals legs on the same match. */
function priceStraddle(
  bets: { over: boolean; under: boolean }[],
  priceOver: number,
  priceUnder: number,
  stake: number,
): StrategyResult {
  let staked = 0;
  let returned = 0;
  let cum = 0;
  let peak = 0;
  let maxDd = 0;
  let wins = 0;
  let streak = 0;
  let worstStreak = 0;

  for (const { over, under } of bets) {
    const cost = stake * 2;
    const ret = (over ? stake / priceOver : 0) + (under ? stake / priceUnder : 0);
    staked += cost;
    returned += ret;
    cum += ret - cost;
    if (ret > cost) {
      wins++;
      streak = 0;
    } else {
      streak++;
      if (streak > worstStreak) worstStreak = streak;
    }
    if (cum > peak) peak = cum;
    if (peak - cum > maxDd) maxDd = peak - cum;
  }

  return {
    key: 'straddle3',
    name: 'Exactly-3 straddle (Over 2.5 + Under 3.5)',
    description:
      'Both totals legs when P(exactly 3 goals) is in its top decile. Wins both only on a 3-goal game.',
    bets: bets.length,
    wins,
    price: 0,
    hasBreakEven: false,
    staked,
    returned,
    maxDrawdown: maxDd,
    longestLosingStreak: worstStreak,
  };
}
